import BaseEntity from '@/entity/BaseEntity';
import { TEMP_WORKER_DOCUMENT_TYPE } from '@/typings/enums/TEMP_WORKER_DOCUMENT_TYPE';
import { getReadableIdentityPieceType, identityPieceType, tempWorkerDocumentDescriptionsCore } from '@/utils/tempWorkerDocument';
import { getEnumKeyFromValue, pickFrom } from '@/utils/tools';
import { forEach } from 'lodash-es';
import { nextTick, reactive } from 'vue';
import useTempWorkerDocumentService from '@/services/temp-worker/TempWorkerDocumentService';
import UserProfileTempWorkerDocumentAsideUpload from '@/components/user-profile/UserProfileTempWorkerDocumentAsideUpload.vue';
import { Bus, useAside } from '@orion.ui/orion';

type DriverLicenceRawType = {
	label: string
	obtainingDate: string
}

type DriverLicenceFormattedType = {
	label: string
	obtainingDate: Date
}

type TempWorkerDataForDocumentUpload = Partial<IAddTempWorkerDocumentModel> & {
	recto?: File
	verso?: File
}

export default class TempWorkerDocumentEntity extends BaseEntity<ITempWorkerDocumentModel> {
	private parentTempWorkerDocumentEntity?: TempWorkerDocumentEntity;
	private readonly privateState = reactive({
		documents: [] as TempWorkerDocumentEntity[],
		originalMetaData: undefined as Undef<string>,
		originalExpirationDate: undefined as Undef<Date>,
	});


	get data () { return this.metaData ? JSON.parse(this.metaData) as Record<string, any> : undefined; }
	get description () { return this.descriptionCore.description; }
	get descriptionCore () { return tempWorkerDocumentDescriptionsCore[this.state.tempWorkerDocumentType]; }
	get documentCreatedDate () { return new Date(this.state.documentCreatedDate); }
	get documentExpiration () { return this.state.documentExpiration; }
	get isNew () { return this.id === 0;}
	get max () { return this.descriptionCore.max; }
	get title () { return this.descriptionCore.title; }
	get userId () { return this.state.userId; }

	get isMultiple () { return this.max !== 1; }
	get isRequired () { return this.descriptionCore.isRequired; }
	get isMissing () {
		let diplomaIsMissing;
		if (this.data && this.tempWorkerDocumentType === TEMP_WORKER_DOCUMENT_TYPE.Diploma) {
			diplomaIsMissing = (this.documents.length === 0);
		}

		return diplomaIsMissing
			? true
			: this.isMultiple ? !this.documents : this.id === 0;
	}

	get dataForApiUpload (): TempWorkerDataForDocumentUpload {
		return {
			...pickFrom(
				this,
				'tempWorkerDocumentType',
				'metaData',
				'userId',
			),
			expirationDate: this.expirationDate?.toPost() ?? undefined,
		};
	}

	get readableTitle () {
		if (this.typeLabel === 'IdentityPiece' && !this.isMissing && this.data?.identityPieceType) {
			return getReadableIdentityPieceType(this.data.identityPieceType);
		} else {
			return this.title;
		}
	}

	get showDocumentTitle () {
		return ['Custom', 'Diploma'].includes(this.typeLabel);
	}

	get metaDataHasChanged () {
		return this.privateState.originalMetaData !== this.metaData;
	}

	get expirationDateHasChanged () {
		return this.privateState.originalExpirationDate?.valueOf() !== this.expirationDate?.valueOf();
	}

	get documentObtainingDate () {
		return this.data?.obtainingDate
			? new Date(this.data?.obtainingDate)
			: undefined;
	}

	set documentObtainingDate (val) {
		const res = {
			...this.data ?? {},
			obtainingDate: val?.toPost(true),
		};

		this.metaData = JSON.stringify(res);
	}

	get driverLicences () {
		return (this.data?.driverLicences as Undef<DriverLicenceRawType[]>)?.map(x => ({
			label: x.label,
			obtainingDate: new Date(x.obtainingDate),
		})).sort((a, b) => a.label.localeCompare(b.label)) ?? [];
	}

	get typeLabel () {
		return getEnumKeyFromValue(TEMP_WORKER_DOCUMENT_TYPE, this.tempWorkerDocumentType);
	}

	get identityType () {
		if (!this.isMissing && this.typeLabel === 'IdentityPiece' && !this.isNew) {
			let type = null;
			forEach(identityPieceType, (v, k) => {
				if (this.data?.identityPieceType === v) {
					type = k;
				}
			});
			return type;
		}
		return null;
	}

	get targetSvg () {
		if (this.isMissing && this.typeLabel === 'IdentityPiece') {
			return 'MissingIdentity';
		} else if (!this.isMissing && this.typeLabel === 'IdentityPiece' && this.identityType) {
			return this.identityType;
		} else {
			return this.typeLabel;
		}
	}

	get isExpired () {
		return !!this.expirationDate && this.expirationDate < new Date();
	};

	get showExpirationDate () {
		return ['IdentityPiece'].includes(this.typeLabel);
	};

	get showDepositDate () {
		return ([
			'CurriculumVitae',
			'SocialCertificate',
			'Iban',
			'AddressProof',
			'Custom',
		].includes(this.typeLabel));
	};

	get showObtainingDate () {
		return ['Diploma'].includes(this.typeLabel);
	};

	get canUploadDoc () {
		return (
			this.self.isTempWorker()
			&& (this.isMissing || (this.isMultiple && this.documents.length < this.max))
		) || (this.isMissing && !this.self.userType);
	}

	get canRequestDoc () {
		return this.self.isAgency() && this.isMissing;
	}


	get tempWorkerDocumentType () { return this.state.tempWorkerDocumentType; }
	set tempWorkerDocumentType (val) { this.state.tempWorkerDocumentType = val; }

	get metaData () { return this.state.metaData; }
	set metaData (val) { this.state.metaData = val; }

	get documents (): TempWorkerDocumentEntity[] { return this.privateState.documents as TempWorkerDocumentEntity[]; }
	set documents (val) {
		val.forEach(doc => doc.connectTempWorkerDocumentEntity(this));
		this.privateState.documents = val;
	}

	get documentTitle () {
		return this.data?.title;
	}

	set documentTitle (val) {
		const res = {
			...this.data ?? {},
			title: val,
		};

		this.metaData = JSON.stringify(res);
	}

	get selectedDriverLicences () {
		return this.driverLicences?.map(x => x.label).sort((a, b) => a.localeCompare(b));
	}

	set selectedDriverLicences (val) {
		const currentlicencesArray = this.driverLicences;
		const newLicencesArray: typeof currentlicencesArray = [];

		if (!!val.length) {
			val.forEach((x) => {
				newLicencesArray.push({
					label: x,
					obtainingDate: currentlicencesArray.findByKey(x, 'label')?.obtainingDate ?? new Date(),
				});
			});
		}

		const res = {
			...this.data ?? {},
			driverLicences: newLicencesArray,
		};
		this.metaData = JSON.stringify(res);
	}

	get identityPieceType () {
		if (this.typeLabel === 'IdentityPiece') {
			return this.data?.identityPieceType;
		}
	};

	set identityPieceType (val) {
		if (this.typeLabel === 'IdentityPiece') {
			const res = {
				...this.data ?? {},
				identityPieceType: val,
			};

			this.metaData = JSON.stringify(res);
			// this.metaData = `{ "identityPieceType": ${val} }`;
		}
	}

	get expirationDate () {
		return this.state.documentExpiration?.expirationDate
			? new Date(this.state.documentExpiration.expirationDate)
			: undefined;
	}

	set expirationDate (val) {
		if (val && this.state) {
			this.state.documentExpiration = {
				expirationDate: val?.toPost(true),
				id: this.state.documentExpiration?.id ?? 0,
			};
		}
	}


	constructor (payload?: ITempWorkerDocumentModel) {
		super(payload);

		this.privateState.originalMetaData = payload?.metaData;
		this.privateState.originalExpirationDate = this.expirationDate;
	};


	view () {
		useTempWorkerDocumentService().view(this.id);
	}

	download () {
		useTempWorkerDocumentService().download(this.id);
	}

	connectTempWorkerDocumentEntity (tempWorkerDocumentEntity: TempWorkerDocumentEntity) {
		this.parentTempWorkerDocumentEntity = tempWorkerDocumentEntity;
	}

	setDriverLicenceObtainingDate (licence: DriverLicenceFormattedType) {
		const stateMetaData = JSON.parse(this.state.metaData ?? '{}');
		const driverLicences = this.driverLicences.filter(x => x.label !== licence.label);

		driverLicences.push(licence);

		stateMetaData.driverLicences = driverLicences;
		this.state.metaData = JSON.stringify(stateMetaData, (key, value) => typeof value === 'undefined' ? null : value);
	}

	async openTempWorkerDocumentUploadModalAsync (forceNew = false) {
		const tempWorkerDocumentEntity = forceNew
			? new TempWorkerDocumentEntity({
				id: 0,
				tempWorkerDocumentType: this.tempWorkerDocumentType,
				userId: this.self.isTempWorker() ? this.self.id : 0,
				documentCreatedDate: new Date().toPost(),
			})
			: this.clone();

		return new Promise<void>((resolve) => {
			useAside({
				Nested: UserProfileTempWorkerDocumentAsideUpload,
				NestedProps: { tempWorkerDocumentEntity },
				events: {
					'leave-end': () => resolve(),

					'delete': async (Aside, payload: number) => {
						Aside._loader()?.show(`Suppression de votre document...`);

						await useTempWorkerDocumentService().deleteDocumentAsync(payload);

						if (this.parentTempWorkerDocumentEntity) {
							this.parentTempWorkerDocumentEntity.deleteChildren(payload);
						} else {
							this.resetEntity();
						}

						nextTick(() => {
							Aside._loader()?.hide();
							Aside.close();
						});
					},

					'upload': async (Aside, payload) => {
						Aside._loader()?.show('Transfert de votre document...');

						const res = await useTempWorkerDocumentService().uploadDocumentAsync(payload);

						Bus.emit('DashboardProfileCompletionCardSetupService:upload', payload.tempWorkerDocumentType);
						Bus.emit('JobOfferAsideDetailsSetupService:upload-new', payload.tempWorkerDocumentType);
						Bus.emit('UserProfileTempWorkerDocumentsSetupService:upload-new');

						nextTick(() => {
							if (res?.data) {
								this.assignData(res.data);
							}
						});

						nextTick(() => {
							Aside._loader()?.hide();
							Aside.close();
						});
					},

					'updateFile': async (Aside, payload) => {
						Aside._loader()?.show('Modification de votre document...');

						await useTempWorkerDocumentService().updateFileAsync(payload);
						this.applyClone();

						nextTick(() => {
							Aside._loader()?.hide();
							Aside.close();
						});
					},

					'updateExpirationDate': async (Aside, payload: Date) => {
						Aside._loader()?.show('Modification de votre document...');

						await useTempWorkerDocumentService().updateExpirationDateAsync(this.id, payload);
						this.applyClone();

						nextTick(() => {
							Aside._loader()?.hide();
							Aside.close();
						});
					},

					'updateMetaData': async (Aside, payload) => {
						Aside._loader()?.show('Modification de votre document...');

						await useTempWorkerDocumentService().updateMetaDataAsync(this.id, payload);
						this.applyClone();

						nextTick(() => {
							Aside._loader()?.hide();
							Aside.close();
						});
					},
				},
			});
		});
	}

	async deleteDocAsync () {
		if (!this.self.isTempWorker()) return;
		await useTempWorkerDocumentService().deleteDocumentAsync(this.id);
		this.resetEntity();
	}

	resetEntity () {
		this.state.id = 0;
		this.state.metaData = undefined;
		this.state.documentCreatedDate = new Date().toPost();
	}

	deleteChildren (id: number) {
		this.privateState.documents.deleteWhere('id', id);
	}

}
